<template lang="pug">
  lei-modal.challenge-modal(
    @close="toggleRelationshipModal(false)"
    :title="modalTitle"
    size="lg"
    :close-with-overlay="false"
    )
    .challenge-modal__info
      p(v-if="actionRemove") {{subInfo}}
      p.challenge-modal__sub-info {{mainInfo}}
    .challenge-modal__input-container
      .challenge-modal__input(v-if="(exceptionPossible || actionAdd) && (actionRemove || actionAdd)")
        challenge-input-block(
          v-if="!actionRemove"
          :disabled="!isRelationEditable"
          :value="parent"
          :json-path="parentPath"
          :old-value="null"
          :label='`Propose Different ${getLabel} Entity`'
          :rules="parentEntityRules"
          @change="value => setNewValue('parent', value)")
        challenge-input-block(
          v-if="!actionRemove"
          :disabled="!isRelationEditable"
          :value="status"
          :json-path="statusPath"
          :old-value="null"
          label="Propose Status"
          @change="value => setNewValue('status', value)")
        challenge-input-block(
          v-if="exceptionPossible && !actionRemove"
          :disabled="!isRelationEditable"
          :value="accountingStandard"
          :json-path="accountingStandardPath"
          :old-value="null"
          label="Propose Accounting Standard"
          @change="value => setNewValue('accountingStandard', value)")
        challenge-input-block(
          v-if="exceptionPossible && !actionAdd"
          :disabled="!isReasonEditable"
          :value="reason"
          :json-path="reasonPath"
          :old-value="null"
          label="Exception Reason"
          :tipWidth="401"
          :errorMsg="exceptionRequired?'Reason is required':''"
          @change="value => setNewValue('reason', value)")
        challenge-input-block(
          v-if="exceptionPossible && !actionAdd"
          :disabled="!isReasonEditable"
          :value="reference"
          :json-path="referencePath"
          :old-value="null"
          label="Exception Reference"
          @change="value => setNewValue('reference', value)")
      .challenge-modal__input(:class="removeFund")
        p.challenge-modal__label Comment
        comment-input(@input="comment=$event" :minRow="commentRow" :container="false")
    .challenge-modal__control(slot="footer")
      lei-btn(type="grey" fill  @click="toggleRelationshipModal(false)") Cancel
      lei-btn(type="cyprus" fill @click="save" :disabled="disabled") Save
</template>
<script>
import _ from 'lodash/string'
import { mapState, mapMutations, mapActions, mapGetters, } from 'vuex'
import {
  RM_FIELD,
  SAVE_FIELD, SET_CURRENT_RELATIONSHIP_TYPE,
  TOGGLE_CHALLENGE_RELATIONSHIP_MODAL,
} from '@/store/modules/record/modules/challenge/mutations-types'
import InputCreator from '../lei-components/inputs/InputCreator'
import LeiModal from '../lei-components/LeiModal'
import LeiBtn from '../lei-components/LeiBtn'
import ChallengeInputBlock from '@/components/challenge-components/ChallengeInputBlock'
import { getFieldIdByPath, } from '@/utils/challenge'
import CommentInput from '@/components/lei-components/inputs/CommentInput'

export default {
  components: {
    CommentInput,
    ChallengeInputBlock,
    LeiBtn,
    LeiModal,
    InputCreator,
  },
  props: {
    action: {
      type: [ String, Boolean, ],
      default: '',
    },
  },
  data () {
    return {
      relationshipType: '',
      parent: '',
      status: '',
      accountingStandard: '',
      reason: '',
      reference: '',
      comment: {
        value: '',
        valid: true,
      },
    }
  },
  computed: {
    parentTypeForOptions () {
      return _.startCase(this.parentType)
    },
    exceptionPossible (){
      return this.parentType === 'directParent' || this.parentType === 'ultimateParent'
    },
    getLabel (){
      if (this.parentType === 'headOffice') return 'Head Office'
      return this.exceptionPossible ? 'Parent' : 'Fund'
    },
    actionAdd (){
      return this.action === 'parent'
    },
    actionRemove (){
      return this.action === 'exception'
    },
    relationOptions () {
      if (this.relationshipType === 'nonExisting') return { nonExisting: this.parentTypeForOptions, }
      return {
        parent: `${this.parentTypeForOptions}  Relationship ${this.exceptionPossible ? 'Accounting Consolidation' : ''}`,
        exception: `${this.parentTypeForOptions}  (Reporting Exception)`,
      }
    },
    relationOptionsFilter () {
      return { enum: Object.values(this.relationOptions), }
    },
    isRelationEditable () {
      return this.relationshipType === 'parent' || this.relationshipType === 'nonExisting'
    },
    isReasonEditable () {
      return this.relationshipType === 'exception'
    },
    subInfo () {
      return `To Remove the ${this.parentTypeForOptions} Relationship, ${this.exceptionPossible ? 'you must specify an exception:' : 'click save:'}`
    },
    mainInfo () {
      if (this.actionAdd) return 'New data'
      return `Add ${this.exceptionPossible ? 'exception' : 'comment'}`
    },
    modalTitle () {
      if (this.actionAdd) return `Add ${this.parentTypeForOptions} Relationship Details`
      if (this.actionRemove) return `Remove ${this.parentTypeForOptions} Relationship Details`
      return `Challenge: ${this.parentTypeForOptions} Relationship Details`
    },
    removeFund (){
      return { 'remove-fund': !this.exceptionPossible && this.actionRemove, }
    },
    rootPath () {
      return this.parentType
    },
    parentPath () {
      return this.createPath('parentEntity', 'info')
    },
    statusPath () {
      return this.createPath('status', 'info')
    },
    reasonPath () {
      return this.createPath('reason', 'exception')
    },
    referencePath () {
      return this.createPath('reference', 'exception')
    },
    accountingStandardPath () {
      return this.createPath('accountingStandard', 'info')
    },
    noChanges () {
      return this.relationshipType === this.getCurrentRelationshipTypes[this.parentType]
        && !this.parent
        && !this.status
        && !this.accountingStandard
        && !this.reason
        && !this.reference
    },
    disabled () {
      if (!this.exceptionPossible && this.actionRemove) return false
      return this.parentEntityRequired
        || this.exceptionRequired
        || !this.comment.valid
        || this.noChanges
    },
    parentEntityRequired (){
      return (this.relationshipType === 'parent' || this.relationshipType === 'nonExisting')
        && (this.getInitialRelationsType[this.parentType] === 'exception' || this.getInitialRelationsType[this.parentType] === 'nonExisting')
        && !this.parent
    },
    exceptionRequired (){
      return this.relationshipType === 'exception'
        && this.getInitialRelationsType[this.parentType] === 'parent'
        && !this.reason
    },
    commentRow () {
      if (this.exceptionPossible) return this.actionAdd ? 11 : 6
      return 6
    },
    getFundStatus () {
      return this.actionRemove ? 'nonExisting' : 'parent'
    },
    parentEntityRules () {
      if (this.parentEntityRequired) {
        return [
          {
            message: `${this.getLabel} Entity is required`,
            trigger: 'change',
            required: true,
          },
        ]
      } else {
        return []
      }
    },

    ...mapGetters('record', [ 'getInitialRelationsType', 'getCurrentRelationshipTypes', ]),
    ...mapState('record/challenge', [ 'parentType', 'challengeRelationshipDetails', ]),
  },
  watch: {
    isRelationEditable (newValue) {
      if (!newValue) {
        this.parent = ''
        this.status = ''
        this.accountingStandard = ''
      }
    },
    isReasonEditable (newValue) {
      if (!newValue) {
        this.reason = ''
        this.reference = ''
      }
    },
  },
  mounted () {
    if (typeof this.action === 'string' && this.exceptionPossible) {
      this.relationshipType = this.action
    } else {
      this.relationshipType = this.getCurrentRelationshipTypes[this.parentType]
    }
  },
  methods: {
    setNewValue (prop, value) {
      this[prop] = value
    },

    async save () {
      this.setCurrentRelationshipType({
        parentType: this.parentType,
        relationshipType: this.exceptionPossible ? this.relationshipType : this.getFundStatus,
      })

      let fieldsToProcess = [
        {
          value: this.parent,
          jsonPath: this.parentPath,
        },
      ]

      if (this.status){
        fieldsToProcess = [
          ...fieldsToProcess,
          ...[
            {
              value: this.status,
              jsonPath: this.statusPath,
            },
          ],
        ]
      }

      if (this.exceptionPossible) {
        fieldsToProcess = [
          ...fieldsToProcess,
          ...[
            {
              value: this.status,
              jsonPath: this.statusPath,
            },
            {
              value: this.accountingStandard,
              jsonPath: this.accountingStandardPath,
            },
            {
              value: this.reason,
              jsonPath: this.reasonPath,
            },
            {
              value: this.reference,
              jsonPath: this.referencePath,
            },
          ],
        ]
      }
      await this.processChallengeChanges(fieldsToProcess)
      this.toggleRelationshipModal(false)
    },

    async processChallengeChanges (fieldsToProcess) {
      for (const fieldToProcess of fieldsToProcess) {
        let { jsonPath, } = fieldToProcess
        let originalValue = this.getValue(jsonPath)
        let proposedValue = fieldToProcess.value
        if (!this.exceptionPossible && this.actionRemove){
          const { xpath, } = await this.getField(getFieldIdByPath(jsonPath))
          this.saveField({
            original_value: originalValue,
            proposed_value: 'removed',
            comment: this.comment.value,
            field: xpath,
            jsonPath: jsonPath,
          })
          continue
        }
        if (!proposedValue) {
          this.rmField(jsonPath)
          continue
        }
        const { xpath, } = await this.getField(getFieldIdByPath(jsonPath))
        this.saveField({
          original_value: originalValue,
          proposed_value: proposedValue,
          comment: this.comment.value,
          field: xpath,
          jsonPath: jsonPath,
        })
      }
    },
    createPath (prop, type) {
      return `$.${this.rootPath}.${type}.${prop}`
    },

    getValue (path) {
      const field = this.challengeRelationshipDetails.find(({ jsonPath, }) => path === jsonPath)
      return field ? field.value : null
    },

    ...mapMutations('record/challenge', {
      toggleRelationshipModal: TOGGLE_CHALLENGE_RELATIONSHIP_MODAL,
      saveField: SAVE_FIELD,
      rmField: RM_FIELD,
      setCurrentRelationshipType: SET_CURRENT_RELATIONSHIP_TYPE,
    }),
    ...mapActions('record/challenge', [ 'getField', ]),
  },
}
</script>
<style lang="stylus">
  @import "../../assets/styles/variables.styl"
  .challenge-modal
    .el-form
      width 100%

    &__type-select
      width 100%
      max-width 100%
      padding 15px 20px 0 20px
      margin 0 0 30px 0

    &__sub-info
      margin 40px 0 20px
      font-weight 600
      color $primary-color
      text-transform uppercase


    &__info
      width 100%
      padding 10px 5% 0
      color $default-color-bold


    &__input
      width 47%
    .remove-fund
      width 100%

    &__label
      font-size 1rem
      color $default-color-bold
      font-weight 600
      height 24px
      margin-bottom 15px

    &__input-container
      display flex
      justify-content space-between
      width 100%
      padding 0 5%

    &__control
      width 100%
      display flex
      justify-content space-between

    .info
      cursor pointer
      margin-left 5px
      font-size 0.9rem

    .container.field
      .title
        font-size 1rem
        margin-bottom 10px
        font-weight 600
        color $default-color-bold

  @media (max-width: $laptop)
    .challenge-modal
      &__type-select
        width 90%
        max-width 90%
        padding 0
      &__info
        padding 0 20px
      &__input-container
        padding 0 20px

      .container
        margin-bottom 25px

        &:last-child
          margin-bottom 0

  @media (max-width: $tablet)
    .challenge-modal
      &__input-container
        flex-direction column
      &__input
        width 100%
      &__label
        margin 15px 0


  .chg-rel-details
    &__type
      padding-bottom 25px
</style>



// WEBPACK FOOTER //
// src/components/modals/ChallengeRelationshipModal.vue